import type { OperatorClass, Person, PresenceDay, RemovedRoleDto, Role } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { humanize, todayIso } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorText,
  Field,
  PageTitle,
  SectionLoader,
  Select,
} from '../../ui';
import { PeopleImport } from './PeopleImport';
import { PeopleRolePicker } from './PeopleRolePicker';
import { PersonPanel, type PersonSection } from './PeoplePersonPanel';
import { useSites } from './SiteScope';

/** Ruoli tolti dalla verifica (doppioni, figure che l'azienda non ha o di un solo progetto): si possono ripristinare. */
function RemovedRoles({ onRestored }: { onRestored: () => void }) {
  const { t } = useTenant();
  const removed = useTenantQuery<{ data: RemovedRoleDto[] }>(
    ['roles-removed'],
    '/onboarding/roles/removed',
  );
  const restore = useMutation({
    mutationFn: (key: string) =>
      api<{ data: RemovedRoleDto[] }>(t(`/onboarding/roles/${encodeURIComponent(key)}/restore`), {
        method: 'POST',
      }),
    onSuccess: onRestored,
  });
  const list = removed.data?.data ?? [];
  if (!list.length) return null;
  return (
    <Card>
      <h2 className="mb-1 text-sm font-semibold">Ruoli tolti</h2>
      <p className="mb-3 text-xs text-ink/55">
        Il sistema li ha tolti perché doppioni, figure che non avete o legate a un solo progetto. Se
        servono, ripristinali: non verranno più tolti.
      </p>
      <ErrorText>{restore.error ? errorMessage(restore.error) : ''}</ErrorText>
      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.chiave} className="text-sm">
            <p className="font-medium">{r.nome}</p>
            {r.motivo && <p className="text-xs text-ink/55">{r.motivo}</p>}
            {r.sostituito_da && (
              <p className="text-xs text-ink/55">
                Unito in: {humanize(r.sostituito_da)} (persone e procedure restano lì).
              </p>
            )}
            <button
              className="mt-1 text-xs text-accent hover:underline disabled:opacity-50"
              disabled={restore.isPending}
              onClick={() => restore.mutate(r.chiave)}
            >
              Ripristina
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}

type Selection = { id: string; section: PersonSection };
type Filter = 'active' | 'pending' | 'inactive' | 'all';
const fold = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('it');

export function People({ importing = false }: { importing?: boolean }) {
  const { tenant, t, isManager, me } = useTenant();
  const qc = useQueryClient();
  const people = useTenantQuery<{ data: Person[] }>(['people'], '/people');
  const roles = useTenantQuery<{ data: Role[] }>(['roles'], '/roles');
  const sites = useSites();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('active');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [creating, setCreating] = useState(false);
  const [tool, setTool] = useState<'presence' | 'import' | 'access' | 'roles' | null>(
    importing ? 'import' : null,
  );
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [feedback, setFeedback] = useState('');
  const panelRef = useRef<HTMLElement>(null);
  const panelHeading = useRef<HTMLHeadingElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const listHeading = useRef<HTMLHeadingElement>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
  const change = (action: () => void) => {
    const apply = () => {
      setDirty(false);
      setFeedback('');
      action();
    };
    if (dirty) {
      returnFocus.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setPending(() => apply);
    } else apply();
  };
  const cancelLeave = () => {
    setPending(null);
    requestAnimationFrame(() => {
      if (returnFocus.current?.isConnected) returnFocus.current.focus();
    });
  };
  const all = people.data?.data ?? [];
  const active = all.filter((person) => person.active);
  const incomplete = (person: Person) => !person.roles.length || !person.user_id;
  const labels = new Map((roles.data?.data ?? []).map((role) => [role.key, role.label]));
  const visible = all
    .filter((person) => {
      if (filter === 'active' && !person.active) return false;
      if (filter === 'inactive' && person.active) return false;
      if (filter === 'pending' && (!person.active || !incomplete(person))) return false;
      return fold(
        [
          person.name,
          person.email ?? '',
          ...person.roles.map((key) => labels.get(key) ?? humanize(key)),
        ].join(' '),
      ).includes(fold(search.trim()));
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'it'));
  const selected = all.find((person) => person.id === selection?.id);
  const open = (
    person: Person,
    section: PersonSection = !person.active
      ? 'details'
      : !person.roles.length
        ? 'roles'
        : !person.user_id
          ? 'account'
          : 'roles',
  ) => {
    if (!creating && selection?.id === person.id && selection.section === section) return;
    change(() => {
      setCreating(false);
      setSelection({ id: person.id, section });
    });
  };
  const close = () =>
    change(() => {
      setCreating(false);
      setSelection(null);
      requestAnimationFrame(() => listHeading.current?.focus());
    });
  const saved = (message: string, section?: PersonSection) => {
    setDirty(false);
    setFeedback(message);
    refresh();
    if (section && selection) setSelection({ ...selection, section });
  };
  useEffect(() => {
    if (!selection && !creating) return;
    panelHeading.current?.focus({ preventScroll: true });
    if (window.matchMedia('(max-width: 1023px)').matches)
      panelHeading.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selection?.id, selection?.section, creating]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return;
    const protectLink = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const link =
        event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
      if (!link || link.target === '_blank') return;
      const target = new URL(link.href, window.location.href);
      if (
        target.origin !== window.location.origin ||
        target.pathname !== window.location.pathname ||
        target.hash === window.location.hash
      )
        return;
      event.preventDefault();
      change(() => {
        window.location.hash = target.hash;
      });
    };
    document.addEventListener('click', protectLink, true);
    return () => document.removeEventListener('click', protectLink, true);
  }, [dirty]);

  const error = people.error ?? roles.error ?? sites.error;
  const loading = people.isLoading || roles.isLoading || sites.isLoading;
  return (
    <>
      <PageTitle
        title="Persone"
        subtitle="Chi lavora nell’azienda, quali ruoli svolge e come accede ai suoi compiti."
        actions={
          isManager ? (
            <div className="flex flex-wrap gap-2">
              <a
                href={`#/t/${tenant.id}/accessi-dati`}
                className="rounded-lg px-3 py-2 text-sm text-accent hover:bg-accent/5"
              >
                Accessi ai dati →
              </a>
              <Button
                disabled={loading || !!error || creating}
                onClick={() =>
                  change(() => {
                    setSelection(null);
                    setCreating(true);
                  })
                }
              >
                Aggiungi persona
              </Button>
            </div>
          ) : undefined
        }
      />
      {loading ? (
        <SectionLoader />
      ) : error ? (
        <Card>
          <ErrorText>{errorMessage(error)}</ErrorText>
          <Button
            className="mt-3"
            variant="ghost"
            onClick={() => {
              void people.refetch();
              void roles.refetch();
              void sites.refetch();
            }}
          >
            Riprova
          </Button>
        </Card>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-black/[0.06] bg-white px-4 py-3 text-sm">
            <p>
              <strong>{active.length}</strong> persone attive
            </p>
            <p
              className={
                active.some((person) => !person.roles.length) ? 'text-amber-800' : 'text-ink/60'
              }
            >
              <strong>{active.filter((person) => !person.roles.length).length}</strong> con ruoli da
              assegnare
            </p>
            <p className="text-ink/60">
              <strong>{active.filter((person) => !person.user_id).length}</strong> con account da
              collegare
            </p>
          </div>
          <div
            className={`grid items-start gap-5 ${selection || creating ? 'lg:grid-cols-[minmax(18rem,.85fr)_minmax(0,1.15fr)]' : ''}`}
          >
            <section
              className="min-w-0 rounded-xl bg-white shadow-[0_0_20px_rgba(8,21,66,0.05)]"
              aria-label="Elenco delle persone"
            >
              <div className="space-y-3 border-b border-black/[0.06] p-4">
                <h2 ref={listHeading} tabIndex={-1} className="font-semibold outline-none">
                  Le persone dell’azienda
                </h2>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-0 flex-1 basis-44">
                    <Field
                      label="Cerca una persona"
                      type="search"
                      placeholder="Nome, email o ruolo"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </div>
                  <div className="min-w-0 flex-1 basis-36">
                    <Select
                      label="Mostra"
                      value={filter}
                      onChange={(event) => setFilter(event.target.value as Filter)}
                    >
                      <option value="active">Persone attive</option>
                      <option value="pending">Da completare</option>
                      <option value="inactive">Disattivate</option>
                      <option value="all">Tutte le persone</option>
                    </Select>
                  </div>
                </div>
                <p aria-live="polite" className="text-xs text-ink/55">
                  {visible.length} {visible.length === 1 ? 'persona' : 'persone'} nell’elenco
                </p>
              </div>
              {!all.length ? (
                <div className="p-4">
                  <Empty>Aggiungi la prima persona e assegna i ruoli che svolge.</Empty>
                </div>
              ) : !visible.length ? (
                <div className="space-y-3 p-4">
                  <Empty>Nessuna persona corrisponde alla ricerca e al filtro.</Empty>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSearch('');
                      setFilter('active');
                    }}
                  >
                    Mostra le persone attive
                  </Button>
                </div>
              ) : (
                <ul className="divide-y divide-black/[0.06]">
                  {visible.map((person) => {
                    const picked = selected?.id === person.id;
                    const place = person.all_sites
                      ? 'Tutte le sedi'
                      : person.sites.length
                        ? person.sites.map((s) => sites.name(s.site_id)).join(', ')
                        : 'Solo attività dell’azienda';
                    const next = !person.active
                      ? 'Apri scheda'
                      : !person.roles.length
                        ? 'Assegna ruoli'
                        : !person.user_id
                          ? 'Configura accesso'
                          : 'Gestisci persona';
                    return (
                      <li key={person.id}>
                        <button
                          type="button"
                          onClick={() => open(person)}
                          aria-label={`${next}: ${person.name}`}
                          aria-expanded={picked}
                          aria-controls={picked ? 'person-panel' : undefined}
                          className={`w-full min-w-0 border-l-4 p-4 text-left transition hover:bg-accent/[0.03] focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-accent ${picked ? 'border-accent bg-accent/[0.04]' : 'border-transparent'}`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              aria-hidden="true"
                              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-accent/10 text-sm font-semibold text-accent"
                            >
                              {person.name
                                .split(/\s+/)
                                .filter(Boolean)
                                .slice(0, 2)
                                .map((word) => word[0])
                                .join('')}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold">{person.name}</p>
                                {person.user_id === me.id && <Badge tone="blue">Tu</Badge>}
                                {!person.active && <Badge>Disattivata</Badge>}
                              </div>
                              {person.email && (
                                <p className="mt-0.5 break-all text-xs text-ink/55">
                                  {person.email}
                                </p>
                              )}
                              <dl className="mt-3 space-y-1.5 text-sm">
                                <div>
                                  <dt className="inline text-ink/50">Ruoli: </dt>
                                  <dd
                                    className={`inline ${person.roles.length ? 'text-ink/80' : 'font-medium text-amber-800'}`}
                                  >
                                    {person.roles.length
                                      ? person.roles
                                          .map((key) => labels.get(key) ?? humanize(key))
                                          .join(', ')
                                      : 'Da assegnare'}
                                  </dd>
                                </div>
                                <div>
                                  <dt className="inline text-ink/50">Ambito: </dt>
                                  <dd className="inline text-ink/75">{place}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-ink/50">Account: </dt>
                                  <dd
                                    className={`inline ${person.user_id ? 'text-emerald-700' : 'text-ink/65'}`}
                                  >
                                    {person.user_id ? 'Collegato' : 'Da collegare'}
                                  </dd>
                                </div>
                              </dl>
                              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                                <span className="text-xs text-ink/50">
                                  {person.open_tasks} compiti aperti
                                </span>
                                {isManager && (
                                  <span className="text-sm font-medium text-accent">
                                    {next} <span aria-hidden="true">→</span>
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
            {(selection || creating) && (
              <section
                ref={panelRef}
                id="person-panel"
                className="min-w-0 scroll-mt-4 rounded-xl border border-accent/15 bg-white p-4 shadow-[0_0_20px_rgba(8,21,66,0.05)] sm:p-5"
              >
                <div className="mb-4 flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink/45">
                      {creating ? 'Nuova persona' : 'Scheda persona'}
                    </p>
                    <h2
                      ref={panelHeading}
                      tabIndex={-1}
                      className="break-words text-lg font-semibold outline-none"
                    >
                      {creating ? 'Aggiungi una persona' : (selected?.name ?? 'Caricamento…')}
                    </h2>
                  </div>
                  <Button variant="ghost" aria-label="Chiudi scheda persona" onClick={close}>
                    Chiudi
                  </Button>
                </div>
                {feedback && (
                  <p
                    role="status"
                    className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                  >
                    {feedback}
                  </p>
                )}
                {creating ? (
                  <NewPerson
                    roles={roles.data?.data ?? []}
                    linked={active.some((person) => person.user_id === me.id)}
                    onDirtyChange={setDirty}
                    onCancel={close}
                    onCreated={(person) => {
                      qc.setQueryData<{ data: Person[] }>(
                        ['tenant', tenant.id, 'people'],
                        (previous) => ({ data: [...(previous?.data ?? []), person] }),
                      );
                      setDirty(false);
                      setCreating(false);
                      setSearch('');
                      setFilter('active');
                      setSelection({
                        id: person.id,
                        section: person.roles.length && sites.sites.length ? 'sites' : 'roles',
                      });
                      setFeedback('Persona aggiunta. Completa ruoli, ambito e accesso.');
                      refresh();
                    }}
                  />
                ) : (
                  selected &&
                  selection && (
                    <PersonPanel
                      key={selected.id}
                      person={selected}
                      roles={roles.data?.data ?? []}
                      section={selection.section}
                      onSectionChange={(section) => open(selected, section)}
                      onDirtyChange={setDirty}
                      hasUnsavedChanges={dirty}
                      onComplete={() => {
                        setDirty(false);
                        setSelection(null);
                        refresh();
                        requestAnimationFrame(() => listHeading.current?.focus());
                      }}
                      onSaved={saved}
                      onChanged={() => {
                        setDirty(false);
                        refresh();
                      }}
                    />
                  )
                )}
              </section>
            )}
          </div>
          {isManager && (
            <details
              open={tool === 'import' || undefined}
              className="mt-6 rounded-xl border border-black/[0.08] bg-white px-4 py-3"
            >
              <summary className="cursor-pointer text-sm font-medium text-ink/65">
                Altre funzioni: presenze, importazione e accessi
              </summary>
              <div className="mt-4 flex flex-wrap gap-2">
                {(
                  [
                    ['presence', 'Presenze di oggi'],
                    ['import', 'Importa persone'],
                    ['access', 'Accessi aggiuntivi'],
                    ['roles', 'Ruoli rimossi'],
                  ] as const
                ).map(([key, label]) => (
                  <Button
                    key={key}
                    variant="ghost"
                    className={tool === key ? 'bg-accent/10 text-accent' : ''}
                    onClick={() => setTool(tool === key ? null : key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <div className="mt-4">
                {tool === 'presence' && <PresenceEditor />}
                {tool === 'import' && <PeopleImport onClose={() => setTool(null)} />}
                {tool === 'access' && <ExtraAccess />}
                {tool === 'roles' && <RemovedRoles onRestored={refresh} />}
              </div>
            </details>
          )}
        </>
      )}
      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink/35 p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="people-unsaved-title"
          aria-describedby="people-unsaved-description"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              cancelLeave();
            }
            if (event.key === 'Tab') {
              const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button');
              const target = event.shiftKey ? buttons[0] : buttons[buttons.length - 1];
              if (document.activeElement === target) {
                event.preventDefault();
                (event.shiftKey ? buttons[buttons.length - 1] : buttons[0])?.focus();
              }
            }
          }}
        >
          <div className="w-full max-w-md space-y-4 rounded-xl bg-white p-5 shadow-xl">
            <h2 id="people-unsaved-title" className="font-semibold">
              Ci sono modifiche non salvate
            </h2>
            <p id="people-unsaved-description" className="text-sm text-ink/65">
              Torna al pulsante di salvataggio per conservare le modifiche, oppure esci senza
              salvarle.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                autoFocus
                onClick={() => {
                  cancelLeave();
                  requestAnimationFrame(() => {
                    const save = panelRef.current?.querySelector<HTMLElement>('[data-person-save]');
                    save?.scrollIntoView({ block: 'center' });
                    save?.focus();
                  });
                }}
              >
                Torna a Salva
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  pending();
                  setPending(null);
                }}
              >
                Esci senza salvare
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NewPerson({
  roles,
  linked,
  onCreated,
  onDirtyChange,
  onCancel,
}: {
  roles: Role[];
  linked: boolean;
  onCreated: (person: Person) => void;
  onDirtyChange: (dirty: boolean) => void;
  onCancel: () => void;
}) {
  const { t } = useTenant();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [klass, setKlass] = useState<OperatorClass>('linea_operativa');
  const [linkMe, setLinkMe] = useState(false);
  useEffect(
    () =>
      onDirtyChange(
        !!(name || email || phone || chosen.length || linkMe || klass !== 'linea_operativa'),
      ),
    [name, email, phone, chosen, linkMe, klass, onDirtyChange],
  );
  const save = useMutation({
    mutationFn: () =>
      api<Person>(t('/people'), {
        body: {
          name: name.trim(),
          roles: chosen,
          operator_class: klass,
          link_me: linkMe,
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      }),
    onSuccess: onCreated,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form className="space-y-4" onSubmit={submit}>
      <p className="text-sm text-ink/60">
        Aggiungi la persona; potrai completare sedi e account nella sua scheda.
      </p>
      <Field
        label="Nome e cognome"
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={120}
        required
        autoComplete="name"
      />
      <Field
        label="Email per l’account (facoltativa)"
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        maxLength={254}
        autoComplete="email"
      />
      <PeopleRolePicker roles={roles} value={chosen} onChange={setChosen} />
      <details>
        <summary className="cursor-pointer text-sm font-medium text-ink/65">Altri dettagli</summary>
        <div className="mt-3 space-y-3">
          <Field
            label="Telefono"
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            maxLength={40}
          />
          <Select
            label="Come riceve il lavoro"
            value={klass}
            onChange={(event) => setKlass(event.target.value as OperatorClass)}
          >
            <option value="linea_operativa">Link personale ai compiti</option>
            <option value="knowledge_worker">Account e assistente AI</option>
          </Select>
        </div>
      </details>
      {!linked && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={linkMe}
            onChange={(event) => setLinkMe(event.target.checked)}
          />
          Questa persona sono io: collega il mio account
        </label>
      )}
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <div className="flex flex-wrap gap-2">
        <Button data-person-save type="submit" disabled={!name.trim() || save.isPending}>
          Crea persona
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Annulla
        </Button>
      </div>
    </form>
  );
}

function PresenceEditor() {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const day = todayIso();
  const q = useTenantQuery<PresenceDay>(['presence', day], `/presence?day=${day}`);
  const [marks, setMarks] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (q.data)
      setMarks(
        Object.fromEntries(
          q.data.data.map((person) => [person.person_id, person.present ?? false]),
        ),
      );
  }, [q.data]);
  const save = useMutation({
    mutationFn: () =>
      api(t('/presence'), {
        method: 'PUT',
        body: {
          day,
          entries: Object.entries(marks).map(([person_id, present]) => ({ person_id, present })),
        },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'presence'] }),
  });
  if (q.isLoading) return <SectionLoader />;
  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Presenze di oggi</h3>
      <p className="text-sm text-ink/60">Chi è assente non riceve nuovi compiti.</p>
      <ErrorText>{q.error || save.error ? errorMessage(q.error ?? save.error) : ''}</ErrorText>
      {q.data?.data.map((person) => (
        <label key={person.person_id} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={marks[person.person_id] ?? false}
            onChange={(event) => setMarks({ ...marks, [person.person_id]: event.target.checked })}
          />
          {person.name}
          {person.present === null && <span className="text-ink/45">· non rilevata</span>}
        </label>
      ))}
      {!!q.data?.data.length && (
        <Button disabled={save.isPending} onClick={() => save.mutate()}>
          Salva presenze
        </Button>
      )}
      {save.isSuccess && (
        <p role="status" className="text-sm text-emerald-700">
          Presenze salvate.
        </p>
      )}
    </div>
  );
}

function ExtraAccess() {
  const { t } = useTenant();
  const [email, setEmail] = useState('');
  const save = useMutation({
    mutationFn: () => api(t('/members'), { body: { email: email.trim() } }),
    onSuccess: () => setEmail(''),
  });
  return (
    <div className="max-w-lg space-y-3">
      <h3 className="font-semibold">Accessi aggiuntivi</h3>
      <p className="text-sm text-ink/60">
        Aggiungi un account già registrato all’azienda. Per l’account personale di un operatore, usa
        la sezione Account della sua scheda.
      </p>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field
          label="Email dell’account già registrato"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" disabled={!email.trim() || save.isPending}>
          Aggiungi accesso
        </Button>
      </form>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      {save.isSuccess && (
        <p role="status" className="text-sm text-emerald-700">
          Accesso aggiunto.
        </p>
      )}
    </div>
  );
}
